import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { encodeBytes } from './bytes';
import { unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * The CONFIGURATION layer: Tarifs and Group Tarifs (architecture §A.2).
 *
 * Immutable, pinned, lockfile semantics. Published versions never change, which is why the
 * version reads below are the most cacheable things in the console and the only mutable spots —
 * a generation's active version — are the ones that carry a precondition.
 */

export function useTarifs(): UseQueryResult<ConfigApi.ListTarifsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.tarifs(),
    queryFn: () => unwrap(config.client.GET('/v1/tarifs')),
  });
}

/**
 * The Partner domain's tag catalog a version's `partnerRoles` may name (SDD-011 P-3, P-4). Read
 * from the same catalog the publish gate vouches roles against, so the role picker cannot offer
 * a role the gate then refuses. Long-lived: tags are another domain's configuration records.
 */
export function usePartnerRoles(): UseQueryResult<ConfigApi.ListPartnerRolesResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.partnerRoles(),
    queryFn: () => unwrap(config.client.GET('/v1/partner-roles')),
    staleTime: Infinity,
  });
}

export function useTarif(slug: string): UseQueryResult<ConfigApi.GetTarifResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.tarif(slug),
    queryFn: () => unwrap(config.client.GET('/v1/tarifs/{slug}', { params: { path: { slug } } })),
  });
}

export function useGeneration(
  tarifSlug: string,
  generation: string,
): UseQueryResult<ConfigApi.GetGenerationResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.generation(tarifSlug, generation),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/generations/{generation}', {
          params: { path: { tarifSlug, generation } },
        }),
      ),
  });
}

/**
 * Every generation of a tarif, ordered by validFrom.
 *
 * Separate from `useGeneration`, and deliberately: the list carries no ResourceVersion, so
 * nothing read from it can be quoted as a publish precondition. A screen that wants to publish
 * reads the ONE generation it is publishing into (§B.10).
 */
export function useGenerations(
  tarifSlug: string,
): UseQueryResult<ConfigApi.ListGenerationsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.generations(tarifSlug),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/generations', {
          params: { path: { tarifSlug } },
        }),
      ),
  });
}

/**
 * Every published version of one generation, oldest first — the version switcher's source, and
 * the pin picker's candidate list.
 *
 * Not `staleTime: Infinity`, unlike a single version: the versions themselves are immutable but
 * the SET is not, since publishing adds to it.
 */
export function useTarifVersions(
  tarifSlug: string,
  generation: string,
): UseQueryResult<ConfigApi.ListVersionsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.versions(tarifSlug, generation),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/generations/{generation}/versions', {
          params: { path: { tarifSlug, generation } },
        }),
      ),
  });
}

export function useTarifVersion(
  tarifSlug: string,
  generation: string,
  version: string,
): UseQueryResult<ConfigApi.GetVersionResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.version(tarifSlug, generation, version),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/generations/{generation}/versions/{version}', {
          params: { path: { tarifSlug, generation, version } },
        }),
      ),
    // A published version is immutable and digest-addressed: re-reading it can only ever return
    // the same bytes (decision T-9).
    staleTime: Infinity,
  });
}

export interface PublishVersionVariables {
  readonly tarifSlug: string;
  readonly generation: string;
  /** The full manifest of exact refs, nothing inline (decision S-4). */
  readonly manifest: Record<string, unknown>;
  /**
   * Whether this publish also makes the new version the active one (prompt 14.5).
   *
   * OMITTED means true, and the omission is load-bearing: the proto field is `optional` precisely
   * so that unset keeps today's behaviour for every existing caller. So the console sends the key
   * only when the operator asked for the staged release — publish now, review, activate later.
   */
  readonly activate?: boolean;
}

/**
 * Publishing runs the gate and moves the generation's active version.
 *
 * No `expectedVersion` in the body: the SDK middleware quotes the generation's ETag as `If-Match`
 * from the read that populated the screen, so a second operator publishing meanwhile is a
 * ConflictError here rather than a silently lost gate report (§B.10).
 */
export function usePublishTarifVersion(): ServiceMutation<
  PublishVersionVariables,
  ConfigApi.PublishVersionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, generation, manifest, activate }: PublishVersionVariables) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/generations/{generation}/versions:publish', {
          params: { path: { tarifSlug, generation } },
          body: { manifest, ...(activate === undefined ? {} : { activate }) },
        }),
      ),
    // Publishing moved the generation's active version and added a version to its list — and
    // nothing else. The published versions themselves are immutable, so there is nothing about
    // any of them to refetch.
    invalidates: ({ tarifSlug, generation }) => [
      apiKeys.config.generation(tarifSlug, generation),
      apiKeys.config.versions(tarifSlug, generation),
    ],
  });
}

/**
 * The gate with no write: what a publish WOULD say (decision S-10).
 *
 * It writes nothing, so it invalidates nothing of its own — but it still re-reads the
 * generation, and that is not tidiness. The SDK's ETag middleware drops a resource's parked
 * ETag after any successful non-safe request, `:validate` included, because it cannot tell a
 * dry run from a write. Without the re-read, the publish that follows would carry no `If-Match`
 * and come back 428 (§B.10). Re-reading restores the precondition the operator is about to
 * quote; the wizard keeps its publish action disabled until it lands.
 */
export function useValidateTarifVersion(): ServiceMutation<
  PublishVersionVariables,
  ConfigApi.ValidateVersionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, generation, manifest }: PublishVersionVariables) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/generations/{generation}/versions:validate', {
          params: { path: { tarifSlug, generation } },
          body: { manifest },
        }),
      ),
    invalidates: ({ tarifSlug, generation }) => [apiKeys.config.generation(tarifSlug, generation)],
  });
}

// ---------------------------------------------------------------------------------------------
// Group Tarifs — the same shape, one layer up: a group version additionally pins an EXACT child
// tarif version, which is what makes the pin chain reproducible (architecture §A.3).
// ---------------------------------------------------------------------------------------------

export function useGroupTarifs(): UseQueryResult<ConfigApi.ListGroupTarifsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupTarifs(),
    queryFn: () => unwrap(config.client.GET('/v1/group-tarifs')),
  });
}

export function useGroupTarif(slug: string): UseQueryResult<ConfigApi.GetGroupTarifResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupTarif(slug),
    queryFn: () =>
      unwrap(config.client.GET('/v1/group-tarifs/{slug}', { params: { path: { slug } } })),
    // An empty slug names no group tarif: callers pass '' to say "not on this screen".
    enabled: slug !== '',
  });
}

export function useGroupGeneration(
  groupTarifSlug: string,
  generation: string,
): UseQueryResult<ConfigApi.GetGroupGenerationResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupGeneration(groupTarifSlug, generation),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}', {
          params: { path: { groupTarifSlug, generation } },
        }),
      ),
  });
}

export function useGroupGenerations(
  groupTarifSlug: string,
): UseQueryResult<ConfigApi.ListGroupGenerationsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupGenerations(groupTarifSlug),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/generations', {
          params: { path: { groupTarifSlug } },
        }),
      ),
    enabled: groupTarifSlug !== '',
  });
}

export function useGroupTarifVersions(
  groupTarifSlug: string,
  generation: string,
): UseQueryResult<ConfigApi.ListGroupVersionsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupVersions(groupTarifSlug, generation),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions', {
          params: { path: { groupTarifSlug, generation } },
        }),
      ),
  });
}

/**
 * The group_core assets a group version may pin: factors, aggregation, billing defaults.
 *
 * Read-only on this surface. The group factor lives in group_core and never in the child config
 * (the motor client's constraint, service summary §3.2), so what a configurator does here is CHOOSE which
 * published asset a version pins — editing one is a different operation on a different resource.
 */
export function useGroupCoreConfigs(
  groupTarifSlug: string,
): UseQueryResult<ConfigApi.ListGroupCoreConfigsResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupCoreConfigs(groupTarifSlug),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/group-core-configs', {
          params: { path: { groupTarifSlug } },
        }),
      ),
  });
}

export interface PublishedTarifVersion {
  readonly ern: string;
  readonly generation: string;
  readonly digest: string;
}

/**
 * Every PUBLISHED version of one tarif, across all its generations — the pin picker's candidates.
 *
 * A group version pins an exact physical child version (decision S-3), and the service refuses a
 * pin that is not published or belongs to another tarif with a 409 (service summary §5.1). This
 * hook is what lets the console offer only the pins that would be accepted, instead of letting an
 * operator author a manifest that is guaranteed to be refused.
 *
 * `useQueries` rather than a loop of `useQuery`: the number of generations is data, and a hook
 * called a data-dependent number of times is not a hook. Each entry keys into the SAME cache slot
 * `useTarifVersions` uses, so opening the picker after visiting the tarif costs nothing.
 *
 * `isPending` is deliberately true while the generation list is still loading. Without that the
 * picker would report "no candidates" for a moment, and "there is nothing to pin" is a very
 * different statement from "we have not looked yet".
 */
export function usePublishedTarifVersions(tarifSlug: string): {
  readonly versions: readonly PublishedTarifVersion[];
  readonly isPending: boolean;
} {
  const { config } = useApiClients();
  const generations = useGenerations(tarifSlug);
  const names = (generations.data?.generations ?? [])
    .map((generation) => generation.name)
    .filter((name): name is string => name !== undefined && name !== '');

  const results = useQueries({
    queries: names.map((generation) => ({
      queryKey: apiKeys.config.versions(tarifSlug, generation),
      queryFn: () =>
        unwrap(
          config.client.GET('/v1/tarifs/{tarifSlug}/generations/{generation}/versions', {
            params: { path: { tarifSlug, generation } },
          }),
        ),
    })),
  });

  const versions = results.flatMap((result, index) =>
    (result.data?.versions ?? [])
      // A version that exists is published (decision S-10: no draft-version entity), but the
      // status is filtered on rather than assumed — the picker's whole promise is that everything
      // in it is pinnable, and a promise that rests on "there is no other case" is not one.
      .filter((version) => version.status === 'ASSET_STATUS_PUBLISHED')
      .map((version) => ({
        ern: version.ern ?? '',
        generation: names[index] ?? '',
        digest: version.digest ?? '',
      }))
      .filter((candidate) => candidate.ern !== ''),
  );

  return {
    versions,
    isPending: generations.isPending || results.some((result) => result.isPending),
  };
}

export function useGroupTarifVersion(
  groupTarifSlug: string,
  generation: string,
  version: string,
): UseQueryResult<ConfigApi.GetGroupVersionResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupVersion(groupTarifSlug, generation, version),
    queryFn: () =>
      unwrap(
        config.client.GET(
          '/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions/{version}',
          { params: { path: { groupTarifSlug, generation, version } } },
        ),
      ),
    staleTime: Infinity,
  });
}

export interface PublishGroupVersionVariables {
  readonly groupTarifSlug: string;
  readonly generation: string;
  readonly manifest: Record<string, unknown>;
  /** Unset means true — see PublishVersionVariables.activate. */
  readonly activate?: boolean;
}

export function usePublishGroupTarifVersion(): ServiceMutation<
  PublishGroupVersionVariables,
  ConfigApi.PublishGroupVersionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({
      groupTarifSlug,
      generation,
      manifest,
      activate,
    }: PublishGroupVersionVariables) =>
      unwrap(
        config.client.POST(
          '/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions:publish',
          {
            params: { path: { groupTarifSlug, generation } },
            body: { manifest, ...(activate === undefined ? {} : { activate }) },
          },
        ),
      ),
    invalidates: ({ groupTarifSlug, generation }) => [
      apiKeys.config.groupGeneration(groupTarifSlug, generation),
      apiKeys.config.groupVersions(groupTarifSlug, generation),
    ],
  });
}

/** The group gate with no write. Re-reads the generation for the same reason the tarif one does. */
export function useValidateGroupTarifVersion(): ServiceMutation<
  PublishGroupVersionVariables,
  ConfigApi.ValidateGroupVersionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ groupTarifSlug, generation, manifest }: PublishGroupVersionVariables) =>
      unwrap(
        config.client.POST(
          '/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions:validate',
          { params: { path: { groupTarifSlug, generation } }, body: { manifest } },
        ),
      ),
    invalidates: ({ groupTarifSlug, generation }) => [
      apiKeys.config.groupGeneration(groupTarifSlug, generation),
    ],
  });
}

// ---------------------------------------------------------------------------------------------
// Authoring (prompt 15). Everything above reads configuration somebody else put there; from here
// down the console is what puts it there.
// ---------------------------------------------------------------------------------------------

export interface CreateTarifVariables {
  /** The identity every manifest, pin and digest will carry — `tarifservice:<slug>`. */
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  /**
   * Claims are filed against this tarif's policies (SDD-018 C-4): every version must then pin a
   * coverage document. Fixed at creation — the API has no update for it. Default false.
   */
  readonly claimsEnabled?: boolean;
}

/**
 * Minting a Tarif.
 *
 * No precondition, and that is not an omission: `If-Match` guards a resource that already exists,
 * and there is nothing here to have read. The service's own uniqueness constraint on the slug is
 * what makes a double submit a 409 rather than two tarifs (decision T-12 governs UPDATES).
 *
 * It invalidates the tarif LIST only. Nothing else in the cache can know about a tarif that did
 * not exist a moment ago, and invalidating the layer would refetch every generation of every
 * other tarif to learn nothing.
 */
export function useCreateTarif(): ServiceMutation<
  CreateTarifVariables,
  ConfigApi.CreateTarifResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ slug, name, description, claimsEnabled }: CreateTarifVariables) =>
      unwrap(
        config.client.POST('/v1/tarifs', {
          body: { slug, name, description, claimsEnabled: claimsEnabled === true },
        }),
      ),
    invalidates: () => [apiKeys.config.tarifs()],
  });
}

export interface CreateGroupTarifVariables {
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  /** The single tarif this group tarif references. Immutable once set (decision D-3). */
  readonly tarifSlug: string;
}

/**
 * Minting a Group Tarif, which is minting a LINK as much as an identity.
 *
 * `tarifSlug` is required by the request and immutable at creation (service summary §3.2), so it
 * is part of the variables rather than something a later update could supply. There is no
 * `UpdateGroupTarif` on this API to supply it with, and that absence is the model rather than a
 * gap: one child definition, zero drift.
 */
export function useCreateGroupTarif(): ServiceMutation<
  CreateGroupTarifVariables,
  ConfigApi.CreateGroupTarifResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ slug, name, description, tarifSlug }: CreateGroupTarifVariables) =>
      unwrap(
        config.client.POST('/v1/group-tarifs', { body: { slug, name, description, tarifSlug } }),
      ),
    invalidates: () => [apiKeys.config.groupTarifs()],
  });
}

/**
 * A tarif's assets, filtered by TYPE.
 *
 * The filter goes into the request (prompt 14.1), not into a predicate over one mixed page. That
 * is what lets a detail screen show "insurance cores" and "tarif & deckungs" as two lists without
 * splitting a page on a string prefix — and a prefix split is exactly the kind of derived rule
 * that survives until somebody names an asset `insurance_core_extras`.
 */
export function useCoreConfigs(
  tarifSlug: string,
  options: { readonly assetType: string },
): UseQueryResult<ConfigApi.ListCoreConfigsResponse> {
  const { config } = useApiClients();
  const { assetType } = options;
  return useQuery({
    queryKey: apiKeys.config.coreConfigs(tarifSlug, assetType),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs', {
          params: { path: { tarifSlug }, query: { assetType } },
        }),
      ),
    enabled: tarifSlug !== '',
  });
}

/**
 * One asset, WITH its ResourceVersion — which the list deliberately does not carry.
 *
 * The editor reads this rather than picking a row out of the list for the reason the publish
 * wizard reads one generation: a write quotes a precondition, and a precondition read off a row
 * in a list is not one (§B.10).
 */
export function useCoreConfig(
  tarifSlug: string,
  name: string,
): UseQueryResult<ConfigApi.GetCoreConfigResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.coreConfig(tarifSlug, name),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs/{name}', {
          params: { path: { tarifSlug, name } },
        }),
      ),
    enabled: name !== '',
  });
}

export interface CreateCoreConfigVariables {
  readonly tarifSlug: string;
  /** `insurance_core.v2` — the version suffix is part of the NAME, not a revision of v1. */
  readonly name: string;
  readonly config: Record<string, unknown>;
  /**
   * Seeds the new draft from an existing asset of the SAME type — the "create the next version
   * from this one" step. Empty creates an empty draft. For an `insurance_core` the service also
   * clones the uploaded files under the new asset's ERNs (SDD-010 §5.1), so a clone is complete
   * without a second request.
   */
  readonly basedOnName?: string;
}

/**
 * A new DRAFT asset. Published assets are immutable (migrations/000002 enforces it with a
 * trigger), so authoring "the next version" is creating a new asset seeded from the old one —
 * which is what `basedOnName` is for, and why the console never offers to edit a published row.
 *
 * No `publishedSchema` any more: since SDD-010 the policy-data schema is DERIVED at publish from
 * the document and its files (C-25), and the field is gone from the request. A client that
 * still sent one would be describing an API nobody serves.
 */
export function useCreateCoreConfig(): ServiceMutation<
  CreateCoreConfigVariables,
  ConfigApi.CreateCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, config: document, basedOnName }) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/core-configs', {
          params: { path: { tarifSlug } },
          body: {
            name,
            config: document,
            ...(basedOnName === undefined || basedOnName === '' ? {} : { basedOnName }),
          },
        }),
      ),
    // Both type lists, because the console cannot know which one the new asset belongs to
    // without parsing its name — and a key derived from a parse is a key that can be wrong.
    invalidates: ({ tarifSlug }) => [apiKeys.config.tarif(tarifSlug)],
  });
}

export interface UpdateCoreConfigVariables {
  readonly tarifSlug: string;
  readonly name: string;
  readonly config: Record<string, unknown>;
}

/**
 * Editing a draft `tarif_deckung`. The SDK quotes the draft's ETag; a published asset answers
 * 409 `ASSET_NOT_EDITABLE`.
 *
 * An `insurance_core` is NOT written here (C-29): its document has two server-owned sections —
 * the named-range and factor headers the file uploads derive — and a blind rewrite would clobber
 * them. It goes through {@link usePutCoreConfigDocument}, and the service refuses this RPC for it.
 */
export function useUpdateCoreConfig(): ServiceMutation<
  UpdateCoreConfigVariables,
  ConfigApi.UpdateCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, config: document }) =>
      unwrap(
        config.client.PATCH('/v1/tarifs/{tarifSlug}/core-configs/{name}', {
          params: { path: { tarifSlug, name } },
          body: { config: document },
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.tarif(tarifSlug),
    ],
  });
}

export interface PublishCoreConfigVariables {
  readonly tarifSlug: string;
  readonly name: string;
}

/**
 * Publishing an asset freezes it and assigns its digest.
 *
 * Irreversible by construction: the row becomes immutable at the database, so there is no
 * "unpublish" to offer and the console must not imply one.
 */
export function usePublishCoreConfig(): ServiceMutation<
  PublishCoreConfigVariables,
  ConfigApi.PublishCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name }: PublishCoreConfigVariables) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/core-configs/{name}:publish', {
          params: { path: { tarifSlug, name } },
          // The precondition rides in the `If-Match` header the SDK parks from the read, never
          // in `expected_version` — one wall, not two spellings of it (§B.10).
          body: {},
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.tarif(tarifSlug),
    ],
  });
}

// ---------------------------------------------------------------------------------------------
// Core Insurance Config v2 (SDD-010): one document, files beside it, a schema derived at publish.
//
// Every write below quotes the ASSET's version (T-12): a file upload changes the document — it
// rewrites the header the file derives — so the asset's ETag, parked by `useCoreConfig`, is the
// one precondition all of them carry. The SDK finds it by walking the URL up to the asset, which
// is why none of these hooks names a version.
// ---------------------------------------------------------------------------------------------

export interface PutCoreConfigDocumentVariables {
  readonly tarifSlug: string;
  readonly name: string;
  /**
   * The WHOLE client-owned document (C-15): `insuredObjects`, `questionGroups`, `premiumItems`.
   * `namedRanges` and `factors` are server-owned and refused if sent (400, `/namedRanges`).
   */
  readonly document: Record<string, unknown>;
}

/**
 * Save-with-report (C-18). A DRAFT is saved even when the report says it would not publish; the
 * response carries the asset as stored AND the report, so the editor places the items on the
 * fields their JSON Pointers name and keeps going. Publish is what refuses.
 */
export function usePutCoreConfigDocument(): ServiceMutation<
  PutCoreConfigDocumentVariables,
  ConfigApi.PutCoreConfigDocumentResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, document }) =>
      unwrap(
        config.client.PUT('/v1/tarifs/{tarifSlug}/core-configs/{name}/document', {
          params: { path: { tarifSlug, name } },
          body: { document },
        }),
      ),
    // The asset and everything derived from it (one prefix, see apiKeys), plus the owner's
    // lists, which render the asset's status.
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
      apiKeys.config.coreConfigs(tarifSlug, 'insurance_core'),
    ],
  });
}

export interface PutCoreConfigDocumentYamlVariables {
  readonly tarifSlug: string;
  readonly name: string;
  /** The request body as YAML text: `document:` at the root, the client sections beneath. */
  readonly yaml: string;
}

/**
 * The same save, as YAML (R-12). The text goes to the gateway as `application/yaml` and the
 * gateway's marshaler turns it into the same request — the console never parses YAML (A-3, C-15).
 * `Accept` names JSON explicitly: without it grpc-gateway answers in the request's content type,
 * and the response would come back as YAML too.
 */
export function usePutCoreConfigDocumentYaml(): ServiceMutation<
  PutCoreConfigDocumentYamlVariables,
  ConfigApi.PutCoreConfigDocumentResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, yaml }) =>
      unwrap(
        config.client.PUT('/v1/tarifs/{tarifSlug}/core-configs/{name}/document', {
          params: { path: { tarifSlug, name } },
          // The typed body is a JSON object; the serializer below sends the text verbatim instead.
          body: yaml as unknown as ConfigApi.PutCoreConfigDocumentRequest,
          bodySerializer: (body) => body as unknown as string,
          headers: { 'Content-Type': 'application/yaml', Accept: 'application/json' },
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
      apiKeys.config.coreConfigs(tarifSlug, 'insurance_core'),
    ],
  });
}

export interface CoreConfigRefVariables {
  readonly tarifSlug: string;
  readonly name: string;
}

/**
 * The full gate (§6) with no write — what a publish would say, said before it. Read-only, so it
 * needs no precondition (api/concurrency-exemptions.json).
 *
 * It still re-reads the ASSET afterwards, for the reason `useValidateTarifVersion` re-reads the
 * generation: the SDK forgets a resource's ETag after any successful request that is not a GET
 * (it cannot tell a check from a write), and the next save would be refused with 428 until
 * something read the asset again. The re-read is that something (§B.10).
 */
export function useValidateCoreConfig(): ServiceMutation<
  CoreConfigRefVariables,
  ConfigApi.ValidateCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name }) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/core-configs/{name}:validate', {
          params: { path: { tarifSlug, name } },
          body: {},
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [apiKeys.config.coreConfig(tarifSlug, name)],
  });
}

/**
 * Discarding a draft. Terminal like publishing — a DISCARDED asset is as immutable as a
 * PUBLISHED one (migrations/000005) — which is why it is a confirmed action and not a delete.
 */
export function useDiscardCoreConfig(): ServiceMutation<
  CoreConfigRefVariables,
  ConfigApi.DiscardCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name }) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/core-configs/{name}:discard', {
          params: { path: { tarifSlug, name } },
          body: {},
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
      apiKeys.config.coreConfigs(tarifSlug, 'insurance_core'),
    ],
  });
}

export interface ValidateCoreConfigExpressionVariables {
  readonly tarifSlug: string;
  readonly name: string;
  readonly expression: string;
  /** Where the expression lives: a premium item at some `order`, or a field of some `object`. */
  readonly context: ConfigApi.ExpressionContext;
}

/**
 * The live-editing check (SPEC §6): one expression, checked against the document as STORED, with
 * positions the editor underlines. A mutation rather than a query because the input changes on
 * every keystroke and is never worth caching; the editor debounces it (Phase 5, 300 ms).
 *
 * Like `useValidateCoreConfig` it re-reads the asset afterwards — one small GET per debounced
 * check — so the ETag the SDK forgot on the POST is back before the operator presses save.
 */
export function useValidateCoreConfigExpression(): ServiceMutation<
  ValidateCoreConfigExpressionVariables,
  ConfigApi.ValidateCoreConfigExpressionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, expression, context }) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/core-configs/{name}:validate-expression', {
          params: { path: { tarifSlug, name } },
          body: { expression, context },
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [apiKeys.config.coreConfig(tarifSlug, name)],
  });
}

/**
 * The document with its files RESOLVED: every named range with its rows, every factor with its
 * rows. What a preview renders; the document alone carries only the headers (SPEC §2.2).
 */
export function useResolvedCoreConfig(
  tarifSlug: string,
  name: string,
): UseQueryResult<ConfigApi.GetResolvedCoreConfigResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.coreConfigResolved(tarifSlug, name),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs/{name}/resolved', {
          params: { path: { tarifSlug, name } },
        }),
      ),
    enabled: name !== '',
  });
}

/**
 * The policy-data schema DERIVED from this asset (C-25): for a PUBLISHED asset the stored one,
 * for a DRAFT the one publishing now would store. `$id` is `<assetERN>/policy-data` (C-30).
 */
export function useCoreConfigPolicyDataSchema(
  tarifSlug: string,
  name: string,
): UseQueryResult<ConfigApi.GetCoreConfigPolicyDataSchemaResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.coreConfigPolicyDataSchema(tarifSlug, name),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs/{name}/policy-data-schema', {
          params: { path: { tarifSlug, name } },
        }),
      ),
    enabled: name !== '',
  });
}

/**
 * The `insurance_core` asset schema the service validates documents against — the SAME bytes as
 * `api/schemas/asset/insurance_core.schema.json`, served so a console can never drift from the
 * service on what a document is. Immutable per deployment, hence never stale.
 */
export function useCoreConfigSchema(): UseQueryResult<ConfigApi.GetCoreConfigSchemaResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.coreConfigSchema(),
    queryFn: () => unwrap(config.client.GET('/v1/core-config-schema')),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

// --- files: analysis (SDD-023) ------------------------------------------------------------------

/** What a tarif file upload is for: a factor (its group) or a lookup file. */
export type ConfigFileKind = 'FACTOR' | 'NAMED_RANGE';

export interface AnalyzeConfigFileVariables {
  readonly tarifSlug: string;
  readonly name: string;
  readonly kind: ConfigFileKind;
  /** FACTOR: the group. */
  readonly group?: string;
  /** NAMED_RANGE: the lookup file's name in the core config. */
  readonly namedRangeFile?: string;
  /** The picked file's own name, its bytes and its content type. */
  readonly fileName: string;
  readonly content: Uint8Array;
  readonly contentType: string;
  /** How the file is read; empty fields are recognised (F-4). */
  readonly options?: ConfigApi.FileReadOptions;
}

/**
 * Analyses a tarif file without storing anything (SDD-023 F-5): the service scans it for viruses,
 * recognises its format, cleans it and checks every row, and answers what it detected, cleaned,
 * warned and found, with a preview of the cleaned table and the `analysisDigest` Confirm quotes.
 * The console never parses the file itself.
 */
export function useAnalyzeConfigFile(): ServiceMutation<
  AnalyzeConfigFileVariables,
  ConfigApi.AnalyzeConfigFileResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, kind, group, namedRangeFile, fileName, content, contentType, options }) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/core-configs/{name}/files:analyze', {
          params: { path: { tarifSlug, name } },
          body: {
            kind: kind === 'FACTOR' ? 'CORE_CONFIG_FILE_KIND_FACTOR' : 'CORE_CONFIG_FILE_KIND_NAMED_RANGE',
            ...(group === undefined ? {} : { group }),
            ...(namedRangeFile === undefined ? {} : { namedRangeFile }),
            fileName,
            content: encodeBytes(content),
            contentType,
            ...(options === undefined ? {} : { options }),
          },
        }),
      ),
    // Nothing is stored — but the SDK's ETag middleware drops the asset's parked ETag after any
    // successful non-safe request, a dry run included (see useValidateTarifVersion). Re-reading
    // the asset restores the precondition the Confirm that follows quotes.
    invalidates: ({ tarifSlug, name }) => [apiKeys.config.coreConfig(tarifSlug, name)],
  });
}

/** What Confirm adds to a file upload (SDD-023 F-5). */
export interface ConfirmedUpload {
  /** The reader options the analysis ran with. */
  readonly options?: ConfigApi.FileReadOptions;
  /** The digest the analysis answered; the service refuses with ANALYSIS_CHANGED if it moved. */
  readonly analysisDigest?: string;
  /** The picked file's own name, stored with the original. */
  readonly originalFileName?: string;
}

const confirmedBody = ({ options, analysisDigest, originalFileName }: ConfirmedUpload) => ({
  ...(options === undefined ? {} : { options }),
  ...(analysisDigest === undefined ? {} : { analysisDigest }),
  ...(originalFileName === undefined ? {} : { originalFileName }),
});

// --- files: named ranges -----------------------------------------------------------------------

export interface PutNamedRangeFileVariables extends ConfirmedUpload {
  readonly tarifSlug: string;
  readonly name: string;
  /** The file's name under the asset: `tarifservice:<tarif>/<asset>/<fileName>` (S-12). */
  readonly fileName: string;
  /** The file's bytes, exactly as picked — a CSV (`range,key,label`) or an XLSX. */
  readonly content: Uint8Array;
  /** `text/csv` or the XLSX type; it decides how the service reads the bytes. */
  readonly contentType: string;
  readonly hasHeader?: boolean;
  readonly headerRows?: number;
  /** Display names for the ranges the file holds; `name` defaults to `code` without one. */
  readonly ranges?: readonly ConfigApi.NamedRangeName[];
}

/**
 * A lookup file uploaded WHOLE (SPEC §4.1). Every range the file holds is (re)derived into the
 * document's `namedRanges[]`, so the response carries the asset as well as the file header. A
 * row that cannot be read answers 400 with one `{path, code, message}` per row (`/rows/<n>/key`).
 */
export function usePutNamedRangeFile(): ServiceMutation<
  PutNamedRangeFileVariables,
  ConfigApi.PutNamedRangeFileResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({
      tarifSlug,
      name,
      fileName,
      content,
      contentType,
      hasHeader,
      headerRows,
      ranges,
      ...confirmed
    }) =>
      unwrap(
        config.client.PUT(
          '/v1/tarifs/{tarifSlug}/core-configs/{name}/named-ranges/files/{fileName}',
          {
            params: { path: { tarifSlug, name, fileName } },
            body: {
              content: encodeBytes(content),
              contentType,
              hasHeader: hasHeader ?? true,
              headerRows: headerRows ?? 1,
              ...(ranges === undefined ? {} : { ranges: [...ranges] }),
              ...confirmedBody(confirmed),
            },
          },
        ),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
    ],
  });
}

export interface PutNamedRangeJsonVariables {
  readonly tarifSlug: string;
  readonly name: string;
  readonly fileName: string;
  /** `{"<code>": [{key, label}, …]}` — serialised to the same CSV and stored as a file. */
  readonly ranges: Record<string, unknown>;
}

/** The JSON alternative to a lookup upload (SPEC §4.1): what the "paste JSON" control sends. */
export function usePutNamedRangeJson(): ServiceMutation<
  PutNamedRangeJsonVariables,
  ConfigApi.PutNamedRangeJsonResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, fileName, ranges }) =>
      unwrap(
        config.client.PUT(
          '/v1/tarifs/{tarifSlug}/core-configs/{name}/named-ranges/files/{fileName}:json',
          {
            params: { path: { tarifSlug, name, fileName } },
            body: { ranges },
          },
        ),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
    ],
  });
}

export interface DeleteNamedRangeFileVariables {
  readonly tarifSlug: string;
  readonly name: string;
  readonly fileName: string;
}

/** Removes a lookup file and every range it derived. A range a field or factor still names fails the next save's report, not this call. */
export function useDeleteNamedRangeFile(): ServiceMutation<
  DeleteNamedRangeFileVariables,
  ConfigApi.DeleteNamedRangeFileResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, fileName }) =>
      unwrap(
        config.client.DELETE(
          '/v1/tarifs/{tarifSlug}/core-configs/{name}/named-ranges/files/{fileName}',
          { params: { path: { tarifSlug, name, fileName } } },
        ),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
    ],
  });
}

/**
 * The stored bytes of a lookup file, base64 in `content` — decode with `decodeBytes`. With
 * `original`, the bytes the user uploaded (SDD-023 F-9); a file saved before SDD-023 has none
 * (404 ORIGINAL_NOT_STORED).
 */
export function useNamedRangeFile(
  tarifSlug: string,
  name: string,
  fileName: string,
  original = false,
): UseQueryResult<ConfigApi.GetNamedRangeFileResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: original
      ? [...apiKeys.config.coreConfigNamedRangeFile(tarifSlug, name, fileName), 'original']
      : apiKeys.config.coreConfigNamedRangeFile(tarifSlug, name, fileName),
    queryFn: () =>
      unwrap(
        config.client.GET(
          '/v1/tarifs/{tarifSlug}/core-configs/{name}/named-ranges/files/{fileName}',
          { params: { path: { tarifSlug, name, fileName }, ...(original ? { query: { original } } : {}) } },
        ),
      ),
    enabled: fileName !== '',
  });
}

/** One named range with its rows — the preview behind a lookup, and an enum field's values. */
export function useNamedRange(
  tarifSlug: string,
  name: string,
  code: string,
): UseQueryResult<ConfigApi.GetNamedRangeResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.coreConfigNamedRange(tarifSlug, name, code),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs/{name}/named-ranges/{code}', {
          params: { path: { tarifSlug, name, code } },
        }),
      ),
    enabled: code !== '',
  });
}

// --- files: factors ------------------------------------------------------------------------------

export interface PutFactorFileVariables extends ConfirmedUpload {
  readonly tarifSlug: string;
  readonly name: string;
  /** The factor's identity, as `factor(group, …)` names it; the file replaces the whole group. */
  readonly group: string;
  readonly content: Uint8Array;
  readonly contentType: string;
  readonly label?: string;
  readonly hasHeader?: boolean;
  readonly headerRows?: number;
}

/**
 * A factor file (`group,label,name,key,value`) uploaded whole, replacing the factor of the same
 * group. Row problems answer 400 with `/rows/<n>/<column>` triples.
 */
export function usePutFactorFile(): ServiceMutation<
  PutFactorFileVariables,
  ConfigApi.PutFactorFileResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({
      tarifSlug,
      name,
      group,
      content,
      contentType,
      label,
      hasHeader,
      headerRows,
      ...confirmed
    }) =>
      unwrap(
        config.client.PUT('/v1/tarifs/{tarifSlug}/core-configs/{name}/factors/{group}', {
          params: { path: { tarifSlug, name, group } },
          body: {
            content: encodeBytes(content),
            contentType,
            ...(label === undefined ? {} : { label }),
            hasHeader: hasHeader ?? true,
            headerRows: headerRows ?? 1,
            ...confirmedBody(confirmed),
          },
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
    ],
  });
}

export interface DeleteFactorVariables {
  readonly tarifSlug: string;
  readonly name: string;
  readonly group: string;
}

export function useDeleteFactor(): ServiceMutation<
  DeleteFactorVariables,
  ConfigApi.DeleteFactorResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, group }) =>
      unwrap(
        config.client.DELETE('/v1/tarifs/{tarifSlug}/core-configs/{name}/factors/{group}', {
          params: { path: { tarifSlug, name, group } },
        }),
      ),
    invalidates: ({ tarifSlug, name }) => [
      apiKeys.config.coreConfig(tarifSlug, name),
      apiKeys.config.coreConfigDerived(tarifSlug, name),
    ],
  });
}

/** The stored bytes of a factor file, base64 in `content`; with `original`, the upload (F-9). */
export function useFactorFile(
  tarifSlug: string,
  name: string,
  group: string,
  original = false,
): UseQueryResult<ConfigApi.GetFactorFileResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: original
      ? [...apiKeys.config.coreConfigFactorFile(tarifSlug, name, group), 'original']
      : apiKeys.config.coreConfigFactorFile(tarifSlug, name, group),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs/{name}/factors/{group}/file', {
          params: { path: { tarifSlug, name, group }, ...(original ? { query: { original } } : {}) },
        }),
      ),
    enabled: group !== '',
  });
}

/** One factor with its rows — the first-50 preview behind an upload. */
export function useFactor(
  tarifSlug: string,
  name: string,
  group: string,
): UseQueryResult<ConfigApi.GetFactorResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.coreConfigFactor(tarifSlug, name, group),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarifs/{tarifSlug}/core-configs/{name}/factors/{group}', {
          params: { path: { tarifSlug, name, group } },
        }),
      ),
    enabled: group !== '',
  });
}

// --- the same four, one layer up ------------------------------------------------------------

export function useGroupCoreConfigsOfType(
  groupTarifSlug: string,
  options: { readonly assetType: string },
): UseQueryResult<ConfigApi.ListGroupCoreConfigsResponse> {
  const { config } = useApiClients();
  const { assetType } = options;
  return useQuery({
    queryKey: apiKeys.config.groupCoreConfigsOfType(groupTarifSlug, assetType),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/group-core-configs', {
          params: { path: { groupTarifSlug }, query: { assetType } },
        }),
      ),
  });
}

export function useGroupCoreConfig(
  groupTarifSlug: string,
  name: string,
): UseQueryResult<ConfigApi.GetGroupCoreConfigResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.groupCoreConfig(groupTarifSlug, name),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/group-core-configs/{name}', {
          params: { path: { groupTarifSlug, name } },
        }),
      ),
    enabled: name !== '',
  });
}

export interface CreateGroupCoreConfigVariables {
  readonly groupTarifSlug: string;
  readonly name: string;
  readonly config: Record<string, unknown>;
  readonly publishedSchema: Record<string, unknown>;
  readonly basedOnName?: string;
}

export function useCreateGroupCoreConfig(): ServiceMutation<
  CreateGroupCoreConfigVariables,
  ConfigApi.CreateGroupCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ groupTarifSlug, name, config: document, publishedSchema, basedOnName }) =>
      unwrap(
        config.client.POST('/v1/group-tarifs/{groupTarifSlug}/group-core-configs', {
          params: { path: { groupTarifSlug } },
          body: {
            name,
            config: document,
            publishedSchema,
            ...(basedOnName === undefined || basedOnName === '' ? {} : { basedOnName }),
          },
        }),
      ),
    invalidates: ({ groupTarifSlug }) => [apiKeys.config.groupTarif(groupTarifSlug)],
  });
}

export interface UpdateGroupCoreConfigVariables {
  readonly groupTarifSlug: string;
  readonly name: string;
  readonly config: Record<string, unknown>;
  readonly publishedSchema: Record<string, unknown>;
}

export function useUpdateGroupCoreConfig(): ServiceMutation<
  UpdateGroupCoreConfigVariables,
  ConfigApi.UpdateGroupCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ groupTarifSlug, name, config: document, publishedSchema }) =>
      unwrap(
        config.client.PATCH('/v1/group-tarifs/{groupTarifSlug}/group-core-configs/{name}', {
          params: { path: { groupTarifSlug, name } },
          body: { config: document, publishedSchema },
        }),
      ),
    invalidates: ({ groupTarifSlug, name }) => [
      apiKeys.config.groupCoreConfig(groupTarifSlug, name),
      apiKeys.config.groupTarif(groupTarifSlug),
    ],
  });
}

export interface PublishGroupCoreConfigVariables {
  readonly groupTarifSlug: string;
  readonly name: string;
}

export function usePublishGroupCoreConfig(): ServiceMutation<
  PublishGroupCoreConfigVariables,
  ConfigApi.PublishGroupCoreConfigResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ groupTarifSlug, name }: PublishGroupCoreConfigVariables) =>
      unwrap(
        config.client.POST('/v1/group-tarifs/{groupTarifSlug}/group-core-configs/{name}:publish', {
          params: { path: { groupTarifSlug, name } },
          body: {},
        }),
      ),
    invalidates: ({ groupTarifSlug, name }) => [
      apiKeys.config.groupCoreConfig(groupTarifSlug, name),
      apiKeys.config.groupTarif(groupTarifSlug),
    ],
  });
}

export interface CreateGenerationVariables {
  readonly tarifSlug: string;
  readonly name: string;
  /** RFC 3339 full-date. A civil date: generations start on a business day (rule 6). */
  readonly validFrom: string;
}

/**
 * Adding a generation, which moves the TARIF's row version (decision T-12).
 *
 * No `expectedVersion` in the body, for the reason no other mutation on this surface carries one:
 * the gateway's interceptor turns the `If-Match` the SDK parked from the read into
 * `expected_version` before the handler sees it, so a REST caller and a gRPC caller reach the same
 * guard. Two spellings of one precondition is how they start disagreeing.
 *
 * The service refuses this with NOTHING_TO_PIN when the owner has no published asset of a type
 * the manifest requires (prompt 14.3). The console does not rely on that refusal to communicate:
 * it disables the action and names the missing type first. The refusal is the wall; the disabled
 * control with a reason is the courtesy.
 */
export function useCreateGeneration(): ServiceMutation<
  CreateGenerationVariables,
  ConfigApi.CreateGenerationResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, name, validFrom }: CreateGenerationVariables) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/generations', {
          params: { path: { tarifSlug } },
          body: { name, validFrom },
        }),
      ),
    // The tarif itself moved (its generation set changed and so did its row version), and the
    // generation list gained an entry. Nothing else in the layer knows about either.
    invalidates: ({ tarifSlug }) => [
      apiKeys.config.tarif(tarifSlug),
      apiKeys.config.generations(tarifSlug),
    ],
  });
}

export interface CreateGroupGenerationVariables {
  readonly groupTarifSlug: string;
  readonly name: string;
  readonly validFrom: string;
}

export function useCreateGroupGeneration(): ServiceMutation<
  CreateGroupGenerationVariables,
  ConfigApi.CreateGroupGenerationResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ groupTarifSlug, name, validFrom }: CreateGroupGenerationVariables) =>
      unwrap(
        config.client.POST('/v1/group-tarifs/{groupTarifSlug}/generations', {
          params: { path: { groupTarifSlug } },
          body: { name, validFrom },
        }),
      ),
    invalidates: ({ groupTarifSlug }) => [
      apiKeys.config.groupTarif(groupTarifSlug),
      apiKeys.config.groupGenerations(groupTarifSlug),
    ],
  });
}

export interface SetActiveVersionVariables {
  readonly tarifSlug: string;
  readonly generation: string;
  /** `v1`, or its full ERN. PUBLISHED, and belonging to THIS generation. */
  readonly version: string;
}

/**
 * Which version is active is a DECISION, not a side effect of the last publish (prompt 14.5).
 *
 * It is the configuration layer's one genuinely mutable pointer, and what it decides is what every
 * NEW instance pins. It moves nothing that already exists: a policy in force stays on the version
 * it was activated under (SSOT §5.3), which is why a rollback is safe and why the confirmation
 * dialog has to say so.
 *
 * The precondition is the GENERATION's, not the version's — published versions are immutable and
 * carry none. Two operators activating different versions from the same read is exactly the race
 * the wall exists for.
 */
export function useSetActiveVersion(): ServiceMutation<
  SetActiveVersionVariables,
  ConfigApi.SetActiveVersionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ tarifSlug, generation, version }: SetActiveVersionVariables) =>
      unwrap(
        config.client.POST('/v1/tarifs/{tarifSlug}/generations/{generation}:activate', {
          params: { path: { tarifSlug, generation } },
          body: { version },
        }),
      ),
    invalidates: ({ tarifSlug, generation }) => [apiKeys.config.generation(tarifSlug, generation)],
  });
}

export interface SetActiveGroupVersionVariables {
  readonly groupTarifSlug: string;
  readonly generation: string;
  readonly version: string;
}

export function useSetActiveGroupVersion(): ServiceMutation<
  SetActiveGroupVersionVariables,
  ConfigApi.SetActiveGroupVersionResponse
> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ groupTarifSlug, generation, version }: SetActiveGroupVersionVariables) =>
      unwrap(
        config.client.POST('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}:activate', {
          params: { path: { groupTarifSlug, generation } },
          body: { version },
        }),
      ),
    invalidates: ({ groupTarifSlug, generation }) => [
      apiKeys.config.groupGeneration(groupTarifSlug, generation),
    ],
  });
}

/**
 * The billing rules in force under a pin pair (SDD-015 §5.5, B-7): the child tarif version's
 * TOTAL `billing_config` with the group tarif version's PARTIAL override laid over it, field by
 * field, and `sources` naming where each value came from (`tarif`, `group`, `default`). Either pin
 * may be given alone — a group version derives its child from its own manifest (DECISIONS A-26).
 *
 * `staleTime: Infinity`: published configuration is immutable; a pair resolves the same way forever.
 */
export function useResolvedBillingConfig(pins: {
  readonly tarifVersionErn?: string;
  readonly groupTarifVersionErn?: string;
}): UseQueryResult<ConfigApi.GetResolvedBillingConfigResponse> {
  const { config } = useApiClients();
  const tarifVersionErn = pins.tarifVersionErn ?? '';
  const groupTarifVersionErn = pins.groupTarifVersionErn ?? '';
  return useQuery({
    queryKey: apiKeys.config.resolvedBillingConfig(tarifVersionErn, groupTarifVersionErn),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/billing-config:resolve', {
          params: {
            query: {
              ...(tarifVersionErn === '' ? {} : { tarifVersionErn }),
              ...(groupTarifVersionErn === '' ? {} : { groupTarifVersionErn }),
            },
          },
        }),
      ),
    staleTime: Infinity,
    enabled: tarifVersionErn !== '' || groupTarifVersionErn !== '',
  });
}

/**
 * The tariff variations and claims configuration a PIN PAIR is covered by (SDD-018 C-5): the group
 * version's override over the tarif version's document, per section, with each section's source.
 * Either ERN alone works — a group version alone resolves through its own child pin. A version that
 * pins no coverage document answers without `config`.
 *
 * `staleTime: Infinity`: published configuration is immutable; a pair resolves the same way forever.
 */
export function useResolvedTarifDeckung(pins: {
  readonly tarifVersionErn?: string | undefined;
  readonly groupTarifVersionErn?: string | undefined;
}): UseQueryResult<ConfigApi.GetResolvedTarifDeckungResponse> {
  const { config } = useApiClients();
  const tarifVersionErn = pins.tarifVersionErn ?? '';
  const groupTarifVersionErn = pins.groupTarifVersionErn ?? '';
  return useQuery({
    queryKey: apiKeys.config.resolvedTarifDeckung(tarifVersionErn, groupTarifVersionErn),
    queryFn: () =>
      unwrap(
        config.client.GET('/v1/tarif-deckung:resolve', {
          params: {
            query: {
              ...(tarifVersionErn === '' ? {} : { tarifVersionErn }),
              ...(groupTarifVersionErn === '' ? {} : { groupTarifVersionErn }),
            },
          },
        }),
      ),
    staleTime: Infinity,
    enabled: tarifVersionErn !== '' || groupTarifVersionErn !== '',
  });
}

/**
 * The roles a `tarif_deckung` approval limit may name (SDD-018 C-2): exactly the catalog the
 * publish gate checks against, so the picker offers nothing the gate would refuse.
 */
export function useApprovalRoles(): UseQueryResult<ConfigApi.ListApprovalRolesResponse> {
  const { config } = useApiClients();
  return useQuery({
    queryKey: apiKeys.config.approvalRoles(),
    queryFn: () => unwrap(config.client.GET('/v1/approval-roles')),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ImportDeckungYamlVariables {
  /** `tarif` → `/v1/tarifs/{slug}/core-configs`, `group` → `/v1/group-tarifs/{slug}/group-core-configs`. */
  readonly owner: 'tarif' | 'group';
  readonly slug: string;
  readonly name: string;
  /** The update body as YAML text: `config:` at the root, the document beneath. */
  readonly yaml: string;
}

/**
 * A `tarif_deckung` imported from a YAML file (ui-spec §3, "Importieren"): the draft is REPLACED by
 * the file's document through the same update the editor's save uses, sent as `application/yaml` —
 * the gateway reads YAML, the console never parses it (A-3, C-15). Nothing is published.
 */
export function useImportDeckungYaml(): ServiceMutation<ImportDeckungYamlVariables, ConfigApi.UpdateCoreConfigResponse> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: ({ owner, slug, name, yaml }) => {
      const init = {
        body: yaml as unknown as ConfigApi.UpdateCoreConfigRequest,
        bodySerializer: (body: unknown) => body as string,
        headers: { 'Content-Type': 'application/yaml', Accept: 'application/json' },
      };
      return owner === 'tarif'
        ? unwrap(
            config.client.PATCH('/v1/tarifs/{tarifSlug}/core-configs/{name}', {
              params: { path: { tarifSlug: slug, name } },
              ...init,
            }),
          )
        : unwrap(
            config.client.PATCH('/v1/group-tarifs/{groupTarifSlug}/group-core-configs/{name}', {
              params: { path: { groupTarifSlug: slug, name } },
              ...(init as unknown as { body: ConfigApi.UpdateGroupCoreConfigRequest }),
              bodySerializer: init.bodySerializer,
              headers: init.headers,
            }),
          );
    },
    invalidates: ({ owner, slug, name }) =>
      owner === 'tarif'
        ? [apiKeys.config.coreConfig(slug, name), apiKeys.config.tarif(slug)]
        : [apiKeys.config.groupCoreConfig(slug, name), apiKeys.config.groupTarif(slug)],
  });
}
